import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Row, Col, Table, Button, Label, Input } from 'reactstrap'
import Select from 'react-select'
import { API_URL } from './utils/Constants/URLS'
import { useSelector } from 'react-redux'

const FocPackList = ({ diId, diData = null, onBack = null }) => {
    const token = localStorage.getItem('token')
    const userDetails = useSelector(store => store.user)

    const [stores, setStores] = useState([])
    const [loadingStores, setLoadingStores] = useState(false)

    const [loadingDI, setLoadingDI] = useState(false)
    const [di, setDi] = useState(diData || null)
    const [lineItems, setLineItems] = useState([])
    const [includedLines, setIncludedLines] = useState({})
    const [selectedQty, setSelectedQty] = useState({})

    const [boxNumber, setBoxNumber] = useState('BOX-001')
    const [selectedStore, setSelectedStore] = useState('')

    const [submitting, setSubmitting] = useState(false)
    const [message, setMessage] = useState(null)

    const getLineId = (line, idx) => line?._id ?? `line-${idx}`
    const getModelName = line =>
        line?.model?.modelName ??
        line?.modelName ??
        line?.name ??
        (line?.model?._id ? line.model._id : line?.model) ??
        '-'
    const getPartCode = line =>
        line?.model?.partCode ?? line?.partCode ?? line?.part_code ?? '-'

    useEffect(() => {
        const getStoreData = async () => {
            try {
                setLoadingStores(true)
                const res = await fetch(`${API_URL}stores`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
                const result = await res.json()
                setStores(result?.data?.docs || [])
            } catch (err) {
                console.error('Error fetching stores:', err)
            } finally {
                setLoadingStores(false)
            }
        }
        if (token) getStoreData()
    }, [token])

    useEffect(() => {
        const fetchDI = async () => {
            if (!diId) return
            if (diData) {
                initFromDI(diData)
                return
            }
            try {
                setLoadingDI(true)
                const res = await fetch(`${API_URL}foc/delivery-instruction/${diId}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                })
                const json = await res.json()
                const payload = json?.data ?? json
                initFromDI(payload)
            } catch (err) {
                console.error('Failed to fetch DI:', err)
            } finally {
                setLoadingDI(false)
            }
        }
        fetchDI()
    }, [diId, diData, token])

    const initFromDI = diObj => {
        if (!diObj) return
        setDi(diObj)

        const items = Array.isArray(diObj.lineItems) ? diObj.lineItems : []
        const normalized = items.map((li, idx) => {
            const lineId = getLineId(li, idx)
            return {
                lineId,
                _id: li._id ?? null,
                model: li.model?._id ?? li.model,
                modelName: getModelName(li),
                partCode: getPartCode(li),
                uom: li?.model?.uom ?? li?.uom ?? '-',
                available: Number(li.quantity ?? 0),
                listPrice: Number(li.listPrice ?? 0),
                discount: Number(li.discount ?? 0),
                rate: Number(li.rate ?? 0),
                standardLeadTime: li.standardLeadTime,
                committedDeliveryDate: li.committedDeliveryDate,
                // Defaults for FOC PL
                narration: '',
                gst: 18,
                value: 0
            }
        })

        setLineItems(normalized)
        const incl = {}
        const qtys = {}
        normalized.forEach(it => {
            incl[it.lineId] = it.available > 0
            qtys[it.lineId] = it.available > 0 ? it.available : 0 // Default to max available
        })
        setIncludedLines(incl)
        setSelectedQty(qtys)

        const diStoreId = diObj?.store?._id ?? diObj?.store ?? ''
        if (diStoreId) setSelectedStore(diStoreId)
        setBoxNumber(prev => prev || 'BOX-001')
    }

    const handleNarrationChange = (lineId, value) => {
        setLineItems(prev =>
            prev.map(li => (li.lineId === lineId ? { ...li, narration: value } : li))
        )
    }

    const handleToggleInclude = lineId => {
        setIncludedLines(prev => ({ ...prev, [lineId]: !prev[lineId] }))
    }

    const handleQtyChange = (lineId, value) => {
        const numeric = Number(value || 0)
        const li = lineItems.find(x => x.lineId === lineId)
        const max = Number(li?.available ?? 0)
        if (numeric > max) {
            setMessage({ type: 'error', text: `Quantity cannot exceed ${max}` })
            return
        }
        setMessage(null)
        setSelectedQty(prev => ({ ...prev, [lineId]: numeric }))
    }

    const handleGstChange = (lineId, value) => {
        setLineItems(prev => prev.map(li => li.lineId === lineId ? { ...li, gst: Number(value || 0) } : li))
    }

    const handleValueChange = (lineId, value) => {
        setLineItems(prev => prev.map(li => li.lineId === lineId ? { ...li, value: Number(value || 0) } : li))
    }

    const handleCreatePackList = async () => {
        setMessage(null)
        if (!selectedStore) {
            setMessage({ type: 'error', text: 'Please select a store.' })
            return
        }
        if (!boxNumber) {
            setMessage({ type: 'error', text: 'Please provide box number.' })
            return
        }

        const payloadLineItems = lineItems
            .filter(li => includedLines[li.lineId])
            .map(li => {
                const qty = Number(selectedQty[li.lineId] ?? 0)
                return {
                    model: li.model,
                    quantity: qty,
                    value: li.value,
                    gst: li.gst,
                    narration: li.narration || ''
                }
            })
            .filter(li => li.quantity > 0)

        if (payloadLineItems.length === 0) {
            setMessage({ type: 'error', text: 'Please include at least one line with quantity > 0.' })
            return
        }

        setSubmitting(true)
        try {
            // 1️⃣ Create the pack list
            const payload = {
                deliveryInstruction: diId,
                store: selectedStore,
                customerName: di?.salesOrder?.lead?.client?.companyName || 'Client Name',
                lineItems: payloadLineItems,
                boxNumber: boxNumber
            }

            const res = await fetch(`${API_URL}foc/pick-list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            })
            const result = await res.json()

            if (result.error) {
                throw new Error(result.message || 'Failed to create Pack List')
            }

            setMessage({ type: 'success', text: 'Pack list created successfully.' })

            // 2️⃣ Build model → picked quantity map
            const modelSubtract = {}
            payloadLineItems.forEach(p => {
                modelSubtract[String(p.model)] =
                    (modelSubtract[String(p.model)] || 0) + Number(p.quantity)
            })

            // 3️⃣ PATCH Delivery Instruction quantities
            try {
                const updatedDILineItems = (
                    Array.isArray(di?.lineItems) ? di.lineItems : []
                ).map(li => {
                    const modelId =
                        li?.model?._id ?? (typeof li?.model === 'string' ? li.model : null)

                    const oldQty = Number(li?.quantity ?? 0)
                    const sub = modelId ? modelSubtract[String(modelId)] || 0 : 0
                    const newQty = Math.max(0, oldQty - sub)

                    // ✅ Build the update payload
                    // CRITICAL: Send only model ID to backend, it will populate the full model object
                    return {
                        _id: li._id,
                        model: modelId, // Send only the ID, backend will populate the full object
                        quantity: newQty,
                        listPrice: li.listPrice,
                        discount: li.discount,
                        rate: li.rate,
                        standardLeadTime: li.standardLeadTime,
                        committedDeliveryDate: li.committedDeliveryDate
                    }
                })

                const diPatchRes = await fetch(
                    `${API_URL}foc/delivery-instruction/${diId}`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify({ lineItems: updatedDILineItems })
                    }
                )

                if (!diPatchRes.ok) {
                    const errText = await diPatchRes.text()
                    console.error('FOC DI update failed:', errText)
                    setMessage({
                        type: 'error',
                        text: 'Pack list created, but failed to update Delivery Instruction quantities.'
                    })
                } else {
                    const patched = await diPatchRes.json()
                    const patchedDI = patched?.data ?? patched

                    if (patchedDI?.lineItems) {
                        setDi(prev => ({ ...(prev || {}), ...(patchedDI || {}) }))
                    } else {
                        setDi(prev => {
                            const base = prev || {}
                            const merged = (
                                Array.isArray(base.lineItems) ? base.lineItems : []
                            ).map(li => {
                                const modelId =
                                    li?.model?._id ??
                                    (typeof li?.model === 'string' ? li.model : null)

                                const sub = modelId ? modelSubtract[String(modelId)] || 0 : 0
                                const oldQty = Number(li?.quantity ?? 0)

                                return { ...li, quantity: Math.max(0, oldQty - sub) }
                            })

                            return { ...base, lineItems: merged }
                        })
                    }
                }
            } catch (e) {
                console.error('Error while PATCHing FOC DI:', e)
                setMessage({
                    type: 'error',
                    text: 'Pack list created, but updating Delivery Instruction failed.'
                })
            }

            // 4️⃣ Update UI availability
            const modelSubtractNums = {}
            payloadLineItems.forEach(p => {
                modelSubtractNums[String(p.model)] =
                    (modelSubtractNums[String(p.model)] || 0) + Number(p.quantity)
            })

            setLineItems(prev =>
                prev.map(li => {
                    const sub = modelSubtractNums[String(li.model)] || 0
                    return { ...li, available: Math.max(0, li.available - sub) }
                })
            )

            setSelectedQty(prev => {
                const copy = { ...prev }
                lineItems.forEach(li => {
                    const sub = modelSubtractNums[String(li.model)] || 0
                    const newAvail = Math.max(0, li.available - sub)
                    if ((copy[li.lineId] ?? 0) > newAvail) {
                        copy[li.lineId] = newAvail > 0 ? 1 : 0
                    }
                })
                return copy
            })

            setIncludedLines(prev => {
                const next = { ...prev }
                lineItems.forEach(li => {
                    const sub = modelSubtractNums[String(li.model)] || 0
                    const newAvail = Math.max(0, li.available - sub)
                    if (newAvail === 0) next[li.lineId] = false
                })
                return next
            })

            // 5️⃣ Prepare for next pack list
            setBoxNumber(`BOX-${String(Date.now()).slice(-5)}`)

            if (onBack) onBack()
            window.location.reload()
        } catch (e) {
            console.error('Error creating PL:', e)
            setMessage({ type: 'error', text: e.message || 'Error creating Pack List' })
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className='w-full'>
            <div className='card shadow-sm'>
                <div className='card-body'>
                    {message && (
                        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
                            {message.text}
                        </div>
                    )}

                    <Row className='g-3 mb-3'>
                        <Col md={4}>
                            <Label className='form-label fw-semibold'>Store</Label>
                            <select
                                className='form-select'
                                value={selectedStore}
                                onChange={e => setSelectedStore(e.target.value)}
                                disabled={submitting}
                            >
                                <option value=''>Select store</option>
                                {stores.map(s => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                            </select>
                        </Col>
                        <Col md={4}>
                            <Label className='form-label fw-semibold'>Box Number</Label>
                            <Input
                                value={boxNumber}
                                onChange={e => setBoxNumber(e.target.value)}
                                disabled={submitting}
                            />
                        </Col>
                    </Row>

                    <div className='table-responsive'>
                        <Table className='table table-striped table-bordered'>
                            <thead>
                                <tr>
                                    <th style={{ width: 80 }}>Include</th>
                                    <th>SI</th>
                                    <th>Name</th>
                                    <th style={{ minWidth: 180 }}>Narration</th>
                                    <th>Part Code</th>
                                    <th>UOM</th>
                                    <th style={{ width: 140 }}>Available</th>
                                    <th style={{ width: 120 }}>Qty</th>
                                    <th>Value</th>
                                    <th>GST %</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lineItems.map((li, idx) => {
                                    const included = !!includedLines[li.lineId]
                                    const curQty = Number(selectedQty[li.lineId] ?? 0)
                                    // Generate options 0 to available
                                    const available = li.available
                                    const qtyOptions = available >= 0 ? Array.from({ length: available + 1 }, (_, i) => i) : [0]

                                    return (
                                        <tr key={li.lineId}>
                                            <td className='text-center'>
                                                <input
                                                    type='checkbox'
                                                    checked={included}
                                                    onChange={() => handleToggleInclude(li.lineId)}
                                                    disabled={submitting}
                                                />
                                            </td>
                                            <td>{idx + 1}</td>
                                            <td>{li.modelName}</td>
                                            <td>
                                                <Input
                                                    type='text'
                                                    placeholder='Enter narration'
                                                    value={li.narration ?? ''}
                                                    onChange={e =>
                                                        handleNarrationChange(li.lineId, e.target.value)
                                                    }
                                                    disabled={submitting}
                                                />
                                            </td>
                                            <td>{li.partCode}</td>
                                            <td>{li.uom}</td>
                                            <td>{available}</td>
                                            <td>
                                                <select
                                                    className='form-select'
                                                    value={curQty}
                                                    onChange={e => handleQtyChange(li.lineId, e.target.value)}
                                                    disabled={!included || submitting}
                                                >
                                                    {qtyOptions.map(q => (
                                                        <option key={q} value={q}>{q}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <input
                                                    type='number'
                                                    className='form-control'
                                                    value={li.value}
                                                    onChange={e => handleValueChange(li.lineId, e.target.value)}
                                                    disabled={!included || submitting}
                                                />
                                            </td>
                                            <td>
                                                <select
                                                    className='form-select'
                                                    value={li.gst}
                                                    onChange={e => handleGstChange(li.lineId, e.target.value)}
                                                    disabled={!included || submitting}
                                                >
                                                    <option value={0}>0%</option>
                                                    <option value={5}>5%</option>
                                                    <option value={12}>12%</option>
                                                    <option value={18}>18%</option>
                                                    <option value={28}>28%</option>
                                                </select>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </Table>
                    </div>

                    <div className='d-flex justify-content-end gap-2 mt-3'>
                        {onBack && (
                            <Button color='secondary' onClick={onBack} disabled={submitting}>
                                Cancel
                            </Button>
                        )}
                        <Button color='primary' onClick={handleCreatePackList} disabled={submitting}>
                            {submitting ? 'Creating...' : 'Create Pack List'}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}

FocPackList.propTypes = {
    diId: PropTypes.string.isRequired,
    diData: PropTypes.object,
    onBack: PropTypes.func
}

export default FocPackList
